/**
 * Karbon360° v2 — Emissions explorer.
 *
 * Slice the footprint by scope, source and facility, and drill from a scope
 * total down to the individual fuel or activity behind it.
 *
 * Everything comes from /api/dashboard's `detailedBreakdown`, which already
 * groups by fuel_key / process_type / category and carries the summed activity
 * and record count per group. Biogenic wood and pellet are shown as a separate
 * memo — never inside the Scope 1 total — matching every other surface.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../components/ErrorBoundary';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import {
  PageHeader, Card, Button, Stat, Badge, Callout, Empty, Skeleton,
  Select, BarRow, fmt,
} from '../components/v2/ui';
import { Donut, MonthlyBars, SCOPE_COLORS } from '../components/v2/ui/Charts';
import { useT } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const n = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v));

/**
 * No 3- or 6-month windows (data scientist, 2026-07-30). In a GHG inventory every
 * comparison is baseline year vs the current year, tracked against the reduction
 * target — 2025 vs 2026, then 2025 vs 2027, and finally 2025 vs 2030. A rolling
 * quarter has no meaning in that model, so offering one invites a comparison the
 * methodology does not support.
 */
const RANGES = [
  { id: 'last-year', labelKey: 'reports.last12' },
  { id: 'last-month', labelKey: 'reports.range.lastMonth' },
  { id: 'all-time', labelKey: 'reports.allTime' },
];

/**
 * The dashboard's detailedBreakdown returns Scope 3 categories with `label`
 * equal to the raw DB key, so they render as "business_travel". (The report's
 * buildReportData humanises them; this endpoint does not.) Map them here rather
 * than showing a database identifier to a user.
 */
const S3_LABEL = {
  business_travel: 'Business travel',
  employee_commuting: 'Employee commuting',
  waste_disposal: 'Waste generated',
  purchased_goods: 'Purchased goods',
  upstream_transport: 'Upstream transport',
  downstream_transport: 'Downstream transport',
};
const s3Label = (c) =>
  S3_LABEL[c.key] || (c.label && c.label !== c.key ? c.label : String(c.key || '').replace(/_/g, ' '));

/** Scope 1 sub-groups, in the order the GHG Protocol presents them. */
const S1_GROUPS = [
  { key: 'stationary', label: 'Stationary combustion' },
  { key: 'mobile', label: 'Mobile combustion' },
  { key: 'process', label: 'Process emissions' },
];

function Group({ title, total, items, color, share, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const rows = items || [];
  if (!rows.length && !total) return null;

  return (
    <div style={{ borderBottom: '1px solid var(--n100)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 0',
          background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left',
        }}
        aria-expanded={open}
      >
        <span style={{ color: 'var(--n400)', display: 'grid', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .18s' }}>
          <X.Chev s={12} />
        </span>
        <span style={{ width: 9, height: 9, borderRadius: 2.5, background: color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 13, color: 'var(--n900)', fontWeight: 500 }}>{title}</span>
        <span style={{ fontSize: 11.5, color: 'var(--n400)' }}>
          {rows.length} source{rows.length === 1 ? '' : 's'}
        </span>
        <span className="k2-num" style={{ fontWeight: 600, minWidth: 76, textAlign: 'right' }}>{fmt(total, 3)}</span>
        <span className="k2-num" style={{ fontSize: 11.5, color: 'var(--n400)', minWidth: 40, textAlign: 'right' }}>
          {share === null ? '' : `${share.toFixed(0)}%`}
        </span>
      </button>

      {open && (
        <div style={{ padding: '2px 0 12px 31px' }}>
          {rows.length === 0
            ? <div style={{ fontSize: 12.5, color: 'var(--n500)', padding: '4px 0' }}>No records in this range.</div>
            : rows.map((it) => (
              <BarRow
                key={it.key}
                name={
                  <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                    <span>{it.label || it.key}</span>
                    <span style={{ fontSize: 11, color: 'var(--n400)' }}>
                      {fmt(n(it.quantity), 0)} {it.unit || ''} · {it.count} record{it.count === 1 ? '' : 's'}
                    </span>
                  </span>
                }
                value={fmt(n(it.emissions_tCO2e), 3)}
                unit="t"
                pct={total > 0 ? (n(it.emissions_tCO2e) / total) * 100 : 0}
                color={color}
              />
            ))}
        </div>
      )}
    </div>
  );
}

function Explorer() {
  const { t } = useT();
  const navigate = useNavigate();
  const [range, setRange] = useState('last-year');
  const [buildingId, setBuildingId] = useState('');
  const [buildings, setBuildings] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/buildings`, auth())
      .then((r) => setBuildings(Array.isArray(r.data) ? r.data : r.data?.buildings || []))
      .catch(() => setBuildings([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = `range=${range}${buildingId ? `&buildingId=${buildingId}` : ''}`;
    axios.get(`${API_URL}/api/dashboard?${qs}`, auth())
      .then((r) => { if (alive) setDash(r.data); })
      .catch((e) => { if (alive) setError(e.response?.data?.message || 'Could not load emissions.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range, buildingId]);

  const db = dash?.detailedBreakdown;
  const split = dash?.kpis?.scopeSplit;

  const totals = useMemo(() => {
    const s1 = S1_GROUPS.reduce((s, g) => s + n(db?.scope1?.[g.key]?.total), 0);
    const s2 = (db?.scope2?.items || []).reduce((s, i) => s + n(i.emissions_tCO2e), 0);
    const s3 = (db?.scope3?.categories || []).reduce((s, c) => s + n(c.emissions_tCO2e), 0);
    return { s1, s2, s3, grand: s1 + s2 + s3, biogenic: n(db?.scope1?.biogenicMemo?.total) };
  }, [db]);

  const donut = useMemo(() => ([
    { name: 'Scope 1 — direct', value: totals.s1, color: SCOPE_COLORS.scope1 },
    { name: 'Scope 2 — purchased energy', value: totals.s2, color: SCOPE_COLORS.scope2 },
    { name: 'Scope 3 — value chain', value: totals.s3, color: SCOPE_COLORS.scope3 },
  ]), [totals]);

  const topSources = useMemo(() => {
    const out = [];
    S1_GROUPS.forEach((g) => (db?.scope1?.[g.key]?.items || []).forEach((i) =>
      out.push({ ...i, scope: 'Scope 1', color: SCOPE_COLORS.scope1 })));
    (db?.scope2?.items || []).forEach((i) => out.push({ ...i, scope: 'Scope 2', color: SCOPE_COLORS.scope2 }));
    (db?.scope3?.categories || []).forEach((c) => (c.items || []).forEach((i) =>
      out.push({ ...i, scope: 'Scope 3', color: SCOPE_COLORS.scope3 })));
    return out.sort((a, b) => n(b.emissions_tCO2e) - n(a.emissions_tCO2e)).slice(0, 8);
  }, [db]);

  if (error) {
    return <div className="k2-wrap"><PageHeader title={t('exp.title')} /><Callout tone="err">{error}</Callout></div>;
  }

  const share = (v) => (totals.grand > 0 ? (v / totals.grand) * 100 : null);

  return (
    <div className="k2-wrap">
      <PageHeader
        title={t('exp.title')}
        desc="Slice the footprint by scope, source and facility, and drill from a scope total down to the fuel or activity behind it."
      >
        <Button icon={X.Doc} onClick={() => navigate('/records')}>Open records</Button>
      </PageHeader>

      <div className="k2-fb">
        <Select value={range} onChange={(e) => setRange(e.target.value)} style={{ width: 'auto', minWidth: 180 }}>
          {RANGES.map((r) => <option key={r.id} value={r.id}>{t(r.labelKey)}</option>)}
        </Select>
        <Select value={buildingId} onChange={(e) => setBuildingId(e.target.value)} style={{ width: 'auto', minWidth: 200 }}>
          <option value="">All facilities</option>
          {buildings.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
        </Select>
        {totals.biogenic > 0 && (
          <Badge tone="ok">{fmt(totals.biogenic, 3)} tCO₂ biogenic — memo, not in totals</Badge>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--n500)' }} className="k2-num">
          {fmt(totals.grand, 3)} tCO₂e total
        </span>
      </div>

      {loading ? (
        <>
          <div className="k2-g4" style={{ marginBottom: 24 }}>
            {[0, 1, 2, 3].map((i) => <Card key={i}><Skeleton h={54} /></Card>)}
          </div>
          <Card><Skeleton h={200} /></Card>
        </>
      ) : totals.grand === 0 ? (
        <Card>
          <Empty
            icon={X.Chart}
            title={t('exp.noEmissions')}
            body={t('exp.noEmissionsBody')}
            action={<Button size="sm" onClick={() => { setRange('all-time'); setBuildingId(''); }}>Reset filters</Button>}
          />
        </Card>
      ) : (
        <>
          <div className="k2-g4" style={{ marginBottom: 24 }}>
            <Stat tone="dark" label={t('exp.total')} value={fmt(totals.grand, 2)} unit="tCO₂e" foot={RANGES.find((r) => r.id === range)?.label} />
            <Stat label={t('overview.scope1')} value={fmt(totals.s1, 2)} unit="tCO₂e" foot={`${(share(totals.s1) ?? 0).toFixed(0)}% of total`} />
            <Stat label={t('overview.scope2')} value={fmt(totals.s2, 2)} unit="tCO₂e" foot={`${(share(totals.s2) ?? 0).toFixed(0)}% of total`} />
            <Stat label={t('overview.scope3')} value={fmt(totals.s3, 2)} unit="tCO₂e" foot={`${(share(totals.s3) ?? 0).toFixed(0)}% of total`} />
          </div>

          <div className="k2-g64" style={{ marginBottom: 24 }}>
            <Card title={t('exp.breakdown')} sub={t('exp.breakdownSub')}>
              <Group
                title={t('exp.s1stationary')}
                total={n(db?.scope1?.stationary?.total)}
                items={db?.scope1?.stationary?.items}
                color={SCOPE_COLORS.scope1}
                share={share(n(db?.scope1?.stationary?.total))}
                defaultOpen
              />
              <Group
                title={t('exp.s1mobile')}
                total={n(db?.scope1?.mobile?.total)}
                items={db?.scope1?.mobile?.items}
                color={SCOPE_COLORS.scope1}
                share={share(n(db?.scope1?.mobile?.total))}
              />
              <Group
                title={t('exp.s1process')}
                total={n(db?.scope1?.process?.total)}
                items={db?.scope1?.process?.items}
                color={SCOPE_COLORS.scope1}
                share={share(n(db?.scope1?.process?.total))}
              />
              <Group
                title={t('exp.s2')}
                total={totals.s2}
                items={db?.scope2?.items}
                color={SCOPE_COLORS.scope2}
                share={share(totals.s2)}
              />
              {(db?.scope3?.categories || []).map((c) => (
                <Group
                  key={c.key}
                  title={`Scope 3 — ${s3Label(c)}`}
                  total={n(c.emissions_tCO2e)}
                  items={c.items}
                  color={SCOPE_COLORS.scope3}
                  share={share(n(c.emissions_tCO2e))}
                />
              ))}
              {totals.biogenic > 0 && (
                <Group
                  title={t('exp.biogenic')}
                  total={totals.biogenic}
                  items={db?.scope1?.biogenicMemo?.items}
                  color={SCOPE_COLORS.biogenic}
                  share={null}
                />
              )}
            </Card>

            <div className="k2-gcol">
              <Card title={t('exp.byScope')} sub={t('exp.byScopeSub')}>
                <Donut data={donut} />
              </Card>
              <Card title={t('exp.largest')} sub={t('exp.totalSub')}>
                {topSources.length === 0
                  ? <Empty icon={X.Chart} title={t('exp.nothingToRank')} />
                  : topSources.map((s2) => (
                    <BarRow
                      key={`${s2.scope}-${s2.key}`}
                      name={
                        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                          <span>{s2.label || s2.key}</span>
                          <span style={{ fontSize: 11, color: 'var(--n400)' }}>{s2.scope}</span>
                        </span>
                      }
                      value={fmt(n(s2.emissions_tCO2e), 3)}
                      unit="t"
                      pct={totals.grand > 0 ? (n(s2.emissions_tCO2e) / totals.grand) * 100 : 0}
                      color={s2.color}
                    />
                  ))}
              </Card>
            </div>
          </div>

          <Card
            title={t('exp.trend')}
            sub={t('exp.trendSub')}
          >
            {(dash?.breakdown || []).length === 0
              ? <Empty icon={X.Chart} title={t('exp.noMonthly')} />
              : <MonthlyBars data={dash.breakdown} />}
          </Card>
        </>
      )}
    </div>
  );
}

export default function ExplorerPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <Explorer />
      </Shell>
    </ErrorBoundary>
  );
}
